import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, Clock, Globe, Pin, X, ArrowRight, Radio } from "lucide-react";
import { getMarkets, getMarketInsights } from "../lib/api";
import { EuropeMap } from "../components/EuropeMap";
import { Loading } from "../components/Loading";
import { Button } from "../components/ui/button";
import { emptyCriteria } from "../lib/options";
import { useSearch } from "../context/SearchContext";
import { eur, km } from "../lib/format";

const ActiveRow = ({ m, stats, onOpen, onPin, pinned }) => (
  <div
    data-testid={`coverage-market-${m.code}`}
    className="flex items-center justify-between gap-2 px-4 py-3 rounded-md border border-border bg-card hover:border-atlas-blue transition-colors"
  >
    <button onClick={onOpen} className="flex items-center gap-3 min-w-0 text-left flex-1">
      <span className="font-mono text-[11px] text-slate-500 border border-border rounded-sm px-1.5 py-0.5 shrink-0">{m.code}</span>
      <span className="text-sm truncate text-foreground">{m.name}</span>
    </button>
    <div className="flex items-center gap-2 shrink-0">
      <span className="hidden sm:flex items-center gap-1 text-xs text-emerald-700">
        <CheckCircle2 className="h-3.5 w-3.5" /> {stats ? `${stats.count} opportunities` : "active"}
      </span>
      <button onClick={onPin} data-testid={`coverage-pin-${m.code}`}
        className={`p-1.5 rounded-sm border transition-colors ${pinned ? "bg-atlas-blue text-white border-atlas-blue" : "border-border text-slate-400 hover:text-atlas-blue hover:border-atlas-blue"}`}
        title={pinned ? "Unpin" : "Pin to compare"}>
        <Pin className="h-3.5 w-3.5" />
      </button>
    </div>
  </div>
);

const SupportedRow = ({ m }) => (
  <div data-testid={`coverage-market-${m.code}`}
    className="flex items-center justify-between gap-2 px-4 py-3 rounded-md border border-border bg-card">
    <div className="flex items-center gap-3 min-w-0">
      <span className="font-mono text-[11px] text-slate-500 border border-border rounded-sm px-1.5 py-0.5 shrink-0">{m.code}</span>
      <span className="text-sm truncate text-foreground">{m.name}</span>
    </div>
    <span className="flex items-center gap-1.5 text-xs text-atlas-blue shrink-0">
      <Radio className="h-3.5 w-3.5" /> Connector ready
    </span>
  </div>
);

const CompareCol = ({ name, s, onOpen }) => (
  <div className="flex-1">
    <div className="font-display text-lg font-semibold mb-3">{name}</div>
    {s ? (
      <>
        <dl className="space-y-2 text-sm">
          <div className="flex justify-between"><dt className="text-muted-foreground">Supply</dt><dd className="font-mono">{s.count} vehicles</dd></div>
          <div className="flex justify-between"><dt className="text-muted-foreground">Avg price</dt><dd className="font-mono">{eur(s.avg_price)}</dd></div>
          <div className="flex justify-between"><dt className="text-muted-foreground">Avg mileage</dt><dd className="font-mono">{km(s.avg_mileage)}</dd></div>
          <div className="flex justify-between"><dt className="text-muted-foreground">Avg Atlas Score</dt><dd className="font-mono">{s.avg_score}</dd></div>
        </dl>
        <Button onClick={onOpen} data-testid={`compare-open-${name}`}
          className="w-full mt-4 bg-atlas-ink hover:bg-atlas-blue text-white">
          View {s.count} exact matches <ArrowRight className="h-4 w-4 ml-1.5" />
        </Button>
      </>
    ) : <p className="text-sm text-muted-foreground">No prototype opportunities in this market yet.</p>}
  </div>
);

const StatBlock = ({ value, label, accent }) => (
  <div>
    <div className={`font-display text-3xl font-bold ${accent ? "text-atlas-sky" : ""}`}>{value}</div>
    <div className="overline text-slate-400">{label}</div>
  </div>
);

export default function Coverage() {
  const navigate = useNavigate();
  const { setCriteria } = useSearch();
  const [data, setData] = useState(null);
  const [stats, setStats] = useState({});
  const [pins, setPins] = useState([]);

  useEffect(() => {
    getMarkets().then(setData);
    getMarketInsights().then((mi) => {
      const map = {};
      (mi.countries || []).forEach((c) => { map[c.country] = c; });
      setStats(map);
    });
  }, []);

  if (!data) return <Loading label="Loading European market coverage…" />;

  const active = data.markets.filter((m) => m.status === "active");
  const supported = data.markets.filter((m) => m.status === "supported");
  const planned = data.markets.filter((m) => m.status === "planned");

  const openMarket = (name) => {
    setCriteria({ ...emptyCriteria(), countries: [name] });
    navigate("/results");
  };

  const togglePin = (name) =>
    setPins((p) => p.includes(name) ? p.filter((x) => x !== name) : p.length >= 2 ? [p[1], name] : [...p, name]);

  return (
    <div>
      <section className="bg-atlas-navy text-white">
        <div className="max-w-[1400px] mx-auto px-5 md:px-8 py-16 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="overline text-atlas-sky mb-3">European market coverage</div>
            <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight">
              <span className="text-gradient-blue">{data.active_markets} active markets</span><br />across one European scope.
            </h1>
            <p className="text-slate-300 mt-5 max-w-xl leading-relaxed">
              Atlas treats Europe as a single scope but is honest about where data flows today.
              {" "}{data.active_markets} markets are <span className="text-white font-medium">active</span> with prototype
              opportunities, a few more are <span className="text-white font-medium">connector-ready</span>, and
              the rest are planned. Pin two active markets to compare them.
            </p>
            <div className="flex gap-8 mt-8">
              <StatBlock value={data.active_markets} label="Active now" accent />
              <StatBlock value={supported.length} label="Connector ready" />
              <StatBlock value={data.total_markets_in_scope} label="In European scope" />
            </div>
          </div>
          <div className="flex justify-center">
            <EuropeMap className="w-full max-w-md h-auto" onSelect={openMarket} ns="cov-" />
          </div>
        </div>
      </section>

      {/* Compare panel */}
      {pins.length > 0 && (
        <section className="border-b border-border bg-slate-50" data-testid="market-compare">
          <div className="max-w-[1400px] mx-auto px-5 md:px-8 py-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-xl font-semibold">Market comparison</h2>
              <Button variant="ghost" onClick={() => setPins([])} data-testid="compare-clear-markets">
                <X className="h-4 w-4 mr-1" /> Clear
              </Button>
            </div>
            <div className="bg-card border border-border rounded-md p-6 flex flex-col sm:flex-row gap-6">
              <CompareCol name={pins[0]} s={stats[pins[0]]} onOpen={() => openMarket(pins[0])} />
              <div className="hidden sm:block w-px bg-border" />
              {pins[1]
                ? <CompareCol name={pins[1]} s={stats[pins[1]]} onOpen={() => openMarket(pins[1])} />
                : <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground border border-dashed border-border rounded-md p-6">Pin a second market to compare</div>}
            </div>
            {pins.length === 2 && stats[pins[0]] && stats[pins[1]] && (
              <p className="text-sm text-muted-foreground mt-3" data-testid="compare-verdict">
                {stats[pins[0]].avg_price <= stats[pins[1]].avg_price ? pins[0] : pins[1]} has the lower average price
                (€{Math.abs(stats[pins[0]].avg_price - stats[pins[1]].avg_price).toLocaleString("en-US")} difference);{" "}
                {stats[pins[0]].count >= stats[pins[1]].count ? pins[0] : pins[1]} has stronger supply.
              </p>
            )}
          </div>
        </section>
      )}

      <div className="max-w-[1400px] mx-auto px-5 md:px-8 py-16 space-y-12">
        <div className="flex flex-wrap items-center gap-6 text-sm">
          <span className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600" /> Active — prototype opportunities now</span>
          <span className="flex items-center gap-2 text-atlas-blue"><Radio className="h-4 w-4" /> Connector ready — next to come online</span>
          <span className="flex items-center gap-2 text-muted-foreground"><Clock className="h-4 w-4 text-slate-400" /> Planned — in scope, no source yet</span>
          <span className="flex items-center gap-2 text-muted-foreground"><Pin className="h-4 w-4 text-atlas-blue" /> Pin up to two active markets</span>
        </div>

        {/* Active markets */}
        <div>
          <h3 className="font-display text-lg font-semibold mb-4" data-testid="coverage-active-heading">
            Active markets <span className="text-muted-foreground font-normal">({active.length})</span>
          </h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="coverage-active-grid">
            {active.map((m) => (
              <ActiveRow key={m.code} m={m} stats={stats[m.name]} pinned={pins.includes(m.name)}
                onOpen={() => openMarket(m.name)} onPin={() => togglePin(m.name)} />
            ))}
          </div>
        </div>

        {/* Next markets (connector ready) */}
        {supported.length > 0 && (
          <div>
            <h3 className="font-display text-lg font-semibold mb-4" data-testid="coverage-supported-heading">
              Next markets — connector ready <span className="text-muted-foreground font-normal">({supported.length})</span>
            </h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="coverage-supported-grid">
              {supported.map((m) => <SupportedRow key={m.code} m={m} />)}
            </div>
          </div>
        )}

        {/* Planned — summarized, not a wall of grey rows */}
        {planned.length > 0 && (
          <div data-testid="coverage-planned">
            <h3 className="font-display text-lg font-semibold mb-2">
              Planned <span className="text-muted-foreground font-normal">({planned.length})</span>
            </h3>
            <p className="text-sm text-muted-foreground mb-4 max-w-2xl">
              {planned.length} further European markets are within Atlas's scope and will be added as sources connect.
              We deliberately don't present them as if they were live today.
            </p>
            <div className="flex flex-wrap gap-1.5">
              {planned.map((m) => (
                <span key={m.code} title={m.name}
                  className="font-mono text-[11px] text-slate-400 border border-dashed border-border rounded-sm px-1.5 py-0.5">
                  {m.code}
                </span>
              ))}
            </div>
          </div>
        )}

        <p className="text-xs text-muted-foreground flex items-center gap-2">
          <Globe className="h-4 w-4" /> Coverage reflects the prototype's simulated sources. No live integrations exist yet.
        </p>
      </div>
    </div>
  );
}
